import React, {useState, useEffect} from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import './Login.css';
const API_URL = import.meta.env.VITE_API_URL;

const Login = ({onLoginSuccess}) => {
  const [mode, setMode] = useState('login');
  const [accountType, setAccountType] = useState(null); // 'user' ou 'entreprise'
  const [resetStep, setResetStep] = useState(1); // 1: demander code, 2: saisir code et nouveau mdp
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    mail: '',
    mdp: '',
    age: '',
    numeroTelephone: '',
    adresse: '',
    region: '',
    ville: '',
    latitude: null,
    longitude: null,
    typeUser: 'user', // par défaut pour l'inscription
    nomEntreprise: '',
    typeEntreprise: '',
    resetCode: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [message, setMessage] = useState('');
  const [locationAvailable, setLocationAvailable] = useState(false);

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const {latitude, longitude} = position.coords;
          setFormData(prev => ({...prev, latitude, longitude}));
          setLocationAvailable(true);

          // Reverse geocoding pour remplir les champs d'adresse
          try {
            const response = await axios.get(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
            const data = response.data;
            const adresse = data.display_name;
            const region = data.address?.state || '';
            const ville = data.address?.city || data.address?.town || data.address?.village || '';
            setFormData(prev => ({...prev, adresse, region, ville}));
          } catch (error) {
            console.error('Erreur reverse geocoding:', error);
          }
        },
        (error) => {
          console.error('Erreur géolocalisation:', error);
          setLocationAvailable(false);
          Swal.fire({
            icon: 'error',
            title: 'Localisation bloquée',
            text: 'Pour vous inscrire, vous devez autoriser l\'accès à votre position. Veuillez activer la géolocalisation dans les paramètres de votre navigateur.',
            confirmButtonText: 'OK'
          });
          setMessage('Localisation requise pour l\'inscription. Veuillez autoriser l\'accès à votre position.');
        }
      );
    } else {
      setLocationAvailable(false);
      Swal.fire({
        icon: 'error',
        title: 'Géolocalisation non supportée',
        text: 'Votre navigateur ne supporte pas la géolocalisation. Pour vous inscrire, un navigateur moderne avec géolocalisation est requis.',
        confirmButtonText: 'OK'
      });
      setMessage('Géolocalisation non supportée par ce navigateur.');
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem('session_expired_alert') === 'true') {
      const customMessage = sessionStorage.getItem('session_expired_message');
      sessionStorage.removeItem('session_expired_alert');
      sessionStorage.removeItem('session_expired_message');
      Swal.fire({
        icon: 'warning',
        title: 'Session expirée',
        text: customMessage || 'Votre session a expiré. Veuillez vous reconnecter.',
        confirmButtonText: 'OK'
      });
    }
  }, []);

  useEffect(() => {
    if (mode === 'register') {
      getLocation();
    }
  }, [mode]);

  const handleToggle = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setAccountType(null); // Réinitialiser le type de compte
    setResetStep(1);
    setFormData({
      nom: '',
      prenom: '',
      mail: '',
      mdp: '',
      age: '',
      numeroTelephone: '',
      adresse: '',
      region: '',
      ville: '',
      typeUser: 'user',
      nomEntreprise: '',
      typeEntreprise: '',
      resetCode: '',
      newPassword: '',
      confirmPassword: ''
    });
    setMessage('');
  };

  const handleChange = (e) => {
    setFormData({...formData, [e.target.name]: e.target.value});
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

   

    // Validation côté client pour l'inscription
    if (mode === 'register') {
      if (!locationAvailable && accountType !== 'entreprise') {
        Swal.fire({
          icon: 'error',
          title: 'Inscription impossible',
          text: 'La localisation est requise pour l\'inscription. Veuillez autoriser l\'accès à votre position dans les paramètres de votre navigateur.',
          confirmButtonText: 'OK'
        });
        return;
      }

      // Validation pour entreprises
      if (accountType === 'entreprise') {
        if (!formData.nomEntreprise) {
          setMessage('Le nom de l\'entreprise est requis.');
          return;
        }
        if (!formData.typeEntreprise) {
          setMessage('Le type d\'entreprise est requis.');
          return;
        }
      }

      if (!formData.nom || !formData.prenom || !formData.mail || !formData.mdp || !formData.numeroTelephone) {
        setMessage('Veuillez remplir tous les champs obligatoires.');
        return;
      }

      if (formData.mdp.length < 6) {
        setMessage('Le mot de passe doit contenir au moins 6 caractères.');
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.mail)) {
        setMessage('Veuillez entrer une adresse email valide.');
        return;
      }

      const phoneRegex = /^[0-9+\-\s()]+$/;
      if (!phoneRegex.test(formData.numeroTelephone)) {
        setMessage('Veuillez entrer un numéro de téléphone valide.');
        return;
      }
    }

    // Validation pour la connexion
    if (mode === 'login') {
      if (!formData.mail || !formData.mdp) {
        setMessage('Veuillez entrer votre email et mot de passe.');
        return;
      }
    }

    try {
      if (mode === 'login') {
        const res = await axios.post(`${API_URL}/auth/login`, {
          mail: formData.mail,
          motDePasse: formData.mdp
        });
        localStorage.setItem('token', res.data.token);
        if (res.data.refreshToken) {
          localStorage.setItem('refreshToken', res.data.refreshToken);
        }
        console.log('Login response:', res.data);
        if (res.data.typeUser) {
          localStorage.setItem('typeUser', res.data.typeUser);
          console.log('TypeUser stored:', res.data.typeUser);
        }
        // Stocker les permissions si elles existent (pour les modérateurs)
        if (res.data.permissions) {
          localStorage.setItem('permissions', JSON.stringify(res.data.permissions));
        } else {
          localStorage.removeItem('permissions'); // Nettoyer si absent
        }
        // Stocker l'ID utilisateur pour les dashboards commercial/installateur
        if (res.data._id || res.data.userId) {
          localStorage.setItem('userId', res.data._id || res.data.userId);
        }
        // Stocker l'horodatage d'activité initiale
        const now = Date.now();
        localStorage.setItem('lastActivityAt', now.toString());
        localStorage.removeItem('sessionCreatedAt');
        localStorage.removeItem('sessionExpiresAt');

        // Déclencher le chargement des configurations d'autorisation & données sensibles
        window.dispatchEvent(new Event('sastra_auth_change'));

        setMessage('Connexion réussie !');
        if (onLoginSuccess) onLoginSuccess(res.data.typeUser || null);
        console.log('Login response data:', res.data); // Debug

      } else if (mode === 'register') {
        // Préparer les données pour l'inscription
        const registerData = {
          nom: formData.nom,
          prenom: formData.prenom,
          mail: formData.mail,
          motDePasse: formData.mdp,
          numeroTelephone: formData.numeroTelephone,
          typeUser: accountType === 'entreprise' ? 'entreprise' : 'user',
          ...(formData.latitude && {latitude: formData.latitude}),
          ...(formData.longitude && {longitude: formData.longitude}),
          ...(formData.age && {age: parseInt(formData.age)}),
          ...(formData.adresse && {adresse: formData.adresse}),
          ...(formData.region && {region: formData.region}),
          ...(formData.ville && {ville: formData.ville})
        };

        // Ajouter les champs entreprise si nécessaire
        if (accountType === 'entreprise') {
          registerData.nomEntreprise = formData.nomEntreprise;
          registerData.typeEntreprise = formData.typeEntreprise;
        }

        const response = await axios.post(`${API_URL}/auth/register`, registerData);

        if (response.data.needsActivation) {
          Swal.fire({
            icon: 'info',
            title: 'Demande enregistrée',
            html: response.data.message,
            confirmButtonText: 'OK'
          });
        } else {
          setMessage('Inscription réussie ! Vous pouvez maintenant vous connecter.');
        }

        setMode('login');
        setAccountType(null);
      } else if (mode === 'forgot') {
        if (resetStep === 1) {
          // Étape 1 : Demander le code (Vérifier si le mail existe)
          await axios.post(`${API_URL}/auth/forgot-password-code`, {mail: formData.mail});
          setMessage('Email trouvé. Code de réinitialisation envoyé.');
          setResetStep(2);
        } else if (resetStep === 2) {
          // Étape 2 : Vérifier si le code est correct
          if (!formData.resetCode) {
            setMessage('Veuillez saisir le code de réinitialisation.');
            return;
          }
          await axios.post(`${API_URL}/auth/verify-reset-code`, {
            mail: formData.mail,
            code: formData.resetCode
          });
          setMessage('Code valide. Vous pouvez maintenant changer votre mot de passe.');
          setResetStep(3);
        } else if (resetStep === 3) {
          // Étape 3 : Changer le mot de passe
          if (!formData.newPassword || !formData.confirmPassword) {
            setMessage('Veuillez saisir le nouveau mot de passe.');
            return;
          }
          if (formData.newPassword !== formData.confirmPassword) {
            setMessage('Les mots de passe ne correspondent pas.');
            return;
          }
          if (formData.newPassword.length < 6) {
            setMessage('Le mot de passe doit contenir au moins 6 caractères.');
            return;
          }

          await axios.post(`${API_URL}/auth/reset-password`, {
            mail: formData.mail,
            code: formData.resetCode,
            newPassword: formData.newPassword
          });

          Swal.fire({
            icon: 'success',
            title: 'Mot de passe réinitialisé',
            text: 'Votre mot de passe a été modifié avec succès. Vous pouvez maintenant vous connecter.',
            confirmButtonText: 'OK'
          });
          setMode('login');
          setResetStep(1);
          setFormData({
            ...formData,
            mail: '',
            mdp: '',
            resetCode: '',
            newPassword: '',
            confirmPassword: ''
          });
        }
      }
    } catch (err) {
      console.error('Erreur complète:', err); // Pour debug

      if (err.response) {
        // Erreur avec réponse du serveur
        const status = err.response.status;
        const errorMessage = err.response.data?.message;

        if (status === 400) {
          // Erreur de validation
          setMessage(errorMessage || 'Données invalides. Vérifiez vos informations.');
        } else if (status === 403) {
          // Compte entreprise non activé
          Swal.fire({
            icon: 'warning',
            title: 'Compte en attente',
            text: errorMessage,
            confirmButtonText: 'OK'
          });
        } else if (status === 409) {
          // Conflit (email déjà existant)
          setMessage('Cet email est déjà utilisé. Veuillez en choisir un autre.');
        } else if (status === 500) {
          // Erreur serveur - essayer d'obtenir plus de détails
          const serverMessage = err.response.data?.error || err.response.data?.message;
          console.error('Erreur serveur détaillée:', err.response.data);
          setMessage(serverMessage || 'Erreur serveur. Veuillez réessayer plus tard.');
        } else {
          setMessage(errorMessage || `Erreur ${status}. Veuillez réessayer.`);
        }
      } else if (err.request) {
        // Erreur réseau
        setMessage('Erreur de connexion. Vérifiez votre connexion internet.');
      } else {
        // Autre erreur
        setMessage('Une erreur inattendue s\'est produite.');
      }
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form">
        <h2>
          {mode === 'login' ? 'Connexion' : mode === 'register' ? 'Inscription' : 'Mot de passe oublié'}
        </h2>

        {message && (
          <div className={`message ${
            message.toLowerCase().includes('succès') || 
            message.toLowerCase().includes('envoyé') || 
            message.toLowerCase().includes('réussie') || 
            message.toLowerCase().includes('valide') ||
            message.toLowerCase().includes('reçu')
            ? 'success' : 'error'}`}>
            {message}
          </div>
        )}

        {/* Sélection du type de compte pour l'inscription */}
        {mode === 'register' && accountType === null && (
          <div className="account-type-selection">
            <p className="selection-title">Je suis :</p>
            <div className="account-type-buttons">
              <button
                type="button"
                className="account-type-btn user-btn"
                onClick={() => {
                  setAccountType('user');
                  setFormData({...formData, typeUser: 'user'});
                  getLocation();
                }}
              >
                <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <span>Particulier</span>
              </button>
              <button
                type="button"
                className="account-type-btn entreprise-btn"
                onClick={() => {
                  setAccountType('entreprise');
                  setFormData({...formData, typeUser: 'entreprise'});
                  setLocationAvailable(true); // Les entreprises n'ont pas besoin de géolocalisation obligatoire
                }}
              >
                <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="9" y1="9" x2="15" y2="9"></line>
                  <line x1="9" y1="15" x2="15" y2="15"></line>
                </svg>
                <span>Entreprise</span>
              </button>
            </div>
            <button
              type="button"
              className="back-btn"
              onClick={() => setMode('login')}
            >
              ← Retour à la connexion
            </button>
          </div>
        )}

        {/* Formulaire d'inscription/connexion */}
        {(mode === 'login' || mode === 'forgot' || (mode === 'register' && accountType !== null)) && (
          <form onSubmit={handleSubmit}>
            {mode === 'register' && (
              <>
                <div className="account-type-badge">
                  <span>
                    {accountType === 'entreprise' ? '🏢 Compte Entreprise' : '👤 Compte Particulier'}
                  </span>
                  <button
                    type="button"
                    className="change-type-btn"
                    onClick={() => {
                      setAccountType(null);
                      setFormData({
                        nom: '',
                        prenom: '',
                        mail: '',
                        mdp: '',
                        age: '',
                        numeroTelephone: '',
                        adresse: '',
                        region: '',
                        ville: '',
                        typeUser: 'user',
                        nomEntreprise: '',
                        typeEntreprise: ''
                      });
                    }}
                  >
                    Changer
                  </button>
                </div>

                {accountType === 'entreprise' && (
                  <>
                    <div className="form-group">
                      <label>Nom de l'entreprise *</label>
                      <input
                        type="text"
                        name="nomEntreprise"
                        value={formData.nomEntreprise}
                        onChange={handleChange}
                        required
                        placeholder="Ex: SASTRA Energy"
                      />
                    </div>

                    <div className="form-group">
                      <label>Type d'entreprise *</label>
                      <select
                        name="typeEntreprise"
                        value={formData.typeEntreprise}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Sélectionner...</option>
                        <option value="PME">PME</option>
                        <option value="Start-up">Start-up</option>
                        <option value="Grande entreprise">Grande entreprise</option>
                        <option value="Association">Association</option>
                        <option value="Autre">Autre</option>
                      </select>
                    </div>
                  </>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label>Nom *</label>
                    <input type="text" name="nom" value={formData.nom} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label>Prénom *</label>
                    <input type="text" name="prenom" value={formData.prenom} onChange={handleChange} required />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Âge</label>
                    <input type="number" name="age" value={formData.age} onChange={handleChange} min="1" max="120" />
                  </div>
                  <div className="form-group">
                    <label>Téléphone *</label>
                    <input type="tel" name="numeroTelephone" value={formData.numeroTelephone} onChange={handleChange} required />
                  </div>
                </div>

                <div className="form-group">
                  <label>Adresse</label>
                  <input type="text" name="adresse" value={formData.adresse} onChange={handleChange} />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Région</label>
                    <input type="text" name="region" value={formData.region} onChange={handleChange} />
                  </div>
                  <div className="form-group">
                    <label>Ville</label>
                    <input type="text" name="ville" value={formData.ville} onChange={handleChange} />
                  </div>
                </div>
              </>
            )}

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="mail"
                value={formData.mail}
                onChange={handleChange}
                required
                autoComplete="email"
                disabled={mode === 'forgot' && resetStep !== 1}
              />
            </div>

            {mode === 'forgot' && resetStep === 2 && (
              <div className="form-group">
                <label>Code de réinitialisation *</label>
                <input
                  type="text"
                  name="resetCode"
                  value={formData.resetCode}
                  onChange={handleChange}
                  required
                  placeholder="Entrez le code reçu par email"
                  maxLength="6"
                />
              </div>
            )}

            {mode === 'forgot' && resetStep === 3 && (
              <>
                <div className="form-group">
                  <label>Nouveau mot de passe *</label>
                  <input
                    type="password"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    required
                    minLength="6"
                    placeholder="Minimum 6 caractères"
                    autoComplete="new-password"
                  />
                </div>

                <div className="form-group">
                  <label>Confirmer le mot de passe *</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    minLength="6"
                    placeholder="Confirmez votre mot de passe"
                    autoComplete="new-password"
                  />
                </div>
              </>
            )}

            {(mode === 'login' || mode === 'register') && (
              <div className="form-group">
                <label>Mot de passe *</label>
                <input
                  type="password"
                  name="mdp"
                  value={formData.mdp}
                  onChange={handleChange}
                  required
                  minLength="6"
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                />
                {mode === 'login' && (
                  <div className="forgot-password">
                    <span className="forgot-link" onClick={() => setMode('forgot')}>Mot de passe oublié ?</span>
                  </div>
                )}
              </div>
            )}

            {mode === 'register' && accountType === 'user' && !locationAvailable && (
              <div className="location-warning">
                ⚠️ La localisation est requise pour l'inscription. Veuillez autoriser l'accès à votre position.
              </div>
            )}

            {mode === 'register' && accountType === 'entreprise' && (
              <div className="info-message">
                ℹ️ Votre compte entreprise sera activé par un administrateur. Vous recevrez un email de confirmation.
              </div>
            )}

            <button
              type="submit"
              className="submit-btn"
              disabled={mode === 'register' && accountType === 'user' && !locationAvailable}
            >
              {mode === 'login' ? 'Se connecter' : 
               mode === 'register' ? "S'inscrire" : 
               resetStep === 1 ? 'Vérifier mon email' : 
               resetStep === 2 ? 'Vérifier le code' : 
               'Réinitialiser le mot de passe'}
            </button>
          </form>
        )}

        <div className="toggle-section">
          {mode === 'forgot' ? (
            <p>
              <span className="toggle-link" onClick={() => {
                setMode('login');
                setResetStep(1);
              }}>Retour à la connexion</span>
              {resetStep > 1 && (
                <>
                  {' | '}
                  <span className="toggle-link" onClick={() => setResetStep(resetStep - 1)}>Retour à l'étape précédente</span>
                </>
              )}
            </p>
          ) : mode === 'register' && accountType === null ? null : (
            <p>
              {mode === 'login' ? 'Pas de compte ? ' : 'Déjà un compte ? '}
              <span className="toggle-link" onClick={handleToggle}>{mode === 'login' ? 'Créer un compte' : 'Se connecter'}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
